import { useCallback, useLayoutEffect, useRef } from 'react'

const FAVORITES_TABLE = '.lbv2__favorites-table'
const FULL_TABLE = '.lbv2__leaderboard-table:not(.lbv2__favorites-table)'
const HEADER_CELLS = 'thead th'
const SYNCED_CLASS = 'is-column-synced'

// Keeps the favorites table's columns as wide as the full table's. Both are
// auto-layout tables sized by their own content, so a long name that only
// one of them contains redistributes that table's columns alone. Rather than
// fixing any column width in the stylesheet, the full table stays the truth:
// its header cells are measured and the favorites table is pinned to them
// (fixed layout, explicit header widths, same table width). Measuring runs in
// layout effects and ResizeObserver callbacks - both before the browser
// paints - so the two tables never show a mismatched frame.
const useColumnSync = (scopeRef, enabled) => {
  const observerRef = useRef(null)
  const publishedRef = useRef('')

  const release = useCallback((favorites) => {
    favorites.classList.remove(SYNCED_CLASS)
    favorites.style.width = ''
    favorites.querySelectorAll(HEADER_CELLS).forEach((cell) => { cell.style.width = '' })
    publishedRef.current = ''
  }, [])

  // Re-observes the current header cells on every pass: a column added or
  // removed re-keys its cell, and a cell nobody observes cannot report a
  // change. The favorites cells are written, never observed, so the writes
  // cannot loop the observer.
  const observe = useCallback((cells) => {
    const observer = observerRef.current

    if (!observer) {
      return
    }

    observer.disconnect()
    cells.forEach((cell) => observer.observe(cell))
  }, [])

  const apply = useCallback(() => {
    const scope = scopeRef.current

    if (!scope) {
      return
    }

    const favorites = scope.querySelector(FAVORITES_TABLE)
    const full = enabled ? scope.querySelector(FULL_TABLE) : null

    if (!full) {
      // No source to follow (full leaderboard collapsed, or a view whose
      // columns are fixed already): the favorites table lays itself out.
      if (favorites) {
        release(favorites)
      }

      observe([])
      return
    }

    const sourceCells = Array.from(full.querySelectorAll(HEADER_CELLS))
    observe(sourceCells)

    if (!favorites) {
      return
    }

    const targetCells = favorites.querySelectorAll(HEADER_CELLS)

    if (!sourceCells.length || sourceCells.length !== targetCells.length) {
      release(favorites)
      return
    }

    // All reads before any write: one layout for the measurements, one for
    // the pinning, instead of a read/write pass per cell.
    const widths = sourceCells.map((cell) => cell.getBoundingClientRect().width)
    const tableWidth = full.getBoundingClientRect().width
    const published = `${tableWidth}|${widths.join(',')}`

    // Most passes find the columns exactly where they were.
    if (published === publishedRef.current) {
      return
    }

    publishedRef.current = published
    targetCells.forEach((cell, index) => { cell.style.width = `${widths[index]}px` })
    favorites.style.width = `${tableWidth}px`
    favorites.classList.add(SYNCED_CLASS)
  }, [ scopeRef, enabled, release, observe ])

  useLayoutEffect(() => {
    observerRef.current = new ResizeObserver(apply)

    return () => {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }, [ apply ])

  // After every render of the scope: it renders only when a table appears or
  // goes (favorites starred, full leaderboard toggled, view mode), which is
  // exactly when the pinning has to be redone. Row content changing under a
  // poll, fonts arriving, images loading, the container resizing - none of
  // which re-render the scope - reach apply through the observer instead.
  useLayoutEffect(apply)
}

export default useColumnSync
